import { useCallback, useEffect, useRef } from 'react'

import { useTranslation } from 'react-i18next'
import BeatLoader from 'react-spinners/BeatLoader'

import noSubscriptions from 'assets/images/no-subscriptions.png'
import { type ICreator } from 'types/interfaces/ICreator'

import Creator from './Creator/Creator'

import './_creatorsList.scss'

interface Props {
    creators: ICreator[]
    heightAdjustment?: number
    renderLocation: string
    selectedItems?: string[]
    onSelect?: (id: string) => void
    onRemoveFromList?: (id: string) => void | Promise<void>
    listSelection?: boolean
    customClass?: string
    creatorRef?: any
    customPlaceholderText?: string | JSX.Element | JSX.Element[]
    tab?: string
    refetchSubscriptions?: any
    searchTerm?: string
    isLoading?: boolean
    onCreatorImpression?: (id?: string) => void
}

const CreatorsList = ({
    creators,
    renderLocation,
    selectedItems,
    onSelect,
    onRemoveFromList,
    listSelection = false,
    customClass,
    creatorRef,
    customPlaceholderText,
    tab,
    refetchSubscriptions,
    searchTerm,
    isLoading = false,
    onCreatorImpression
}: Props) => {
    const { t } = useTranslation()

    const listRef = useRef<HTMLDivElement | null>(null)
    const itemRefs = useRef<Map<string, HTMLDivElement>>(new Map())

    const getScrollRoot = useCallback(() => {
        return listRef.current?.closest('.browse-creators-whs') as HTMLElement | null
    }, [])

    const reportVisibleCreators = useCallback(() => {
        const root = getScrollRoot()

        if (!root || !onCreatorImpression) return

        const rootRect = root.getBoundingClientRect()

        const visibleTop = Math.max(rootRect.top, 0)
        const visibleBottom = Math.min(rootRect.bottom, window.innerHeight)

        itemRefs.current.forEach((node, userId) => {
            const rect = node.getBoundingClientRect()

            if (!rect.height) return

            const visibleHeight =
                Math.min(rect.bottom, visibleBottom) -
                Math.max(rect.top, visibleTop)

            const visibleRatio = Math.max(0, visibleHeight) / rect.height

            if (visibleRatio >= 0.75) {
                onCreatorImpression(userId)
            }
        })
    }, [getScrollRoot, onCreatorImpression])

    useEffect(() => {
        const root = getScrollRoot()

        if (!root) return

        const run = () => {
            requestAnimationFrame(reportVisibleCreators)
        }

        run()

        root.addEventListener('scroll', run, { passive: true })
        window.addEventListener('resize', run)

        return () => {
            root.removeEventListener('scroll', run)
            window.removeEventListener('resize', run)
        }
    }, [creators, tab, getScrollRoot, reportVisibleCreators])

    return (
        <div
            ref={listRef}
            className={`creatorsList ${customClass ?? ''}`}
        >
            {isLoading ? (
                <div className='creatorsList__loading'>
                    <BeatLoader size={12} />
                </div>
            ) : creators.length > 0 ? (
                creators.map((creator, idx) => {
                    const isLast = idx === creators.length - 1
                    const userId = creator?.userId

                    return (
                        <div
                            className='creator'
                            ref={node => {
                                if (isLast && creatorRef) {
                                    creatorRef(node)
                                }

                                if (!userId) return

                                if (node) {
                                    itemRefs.current.set(userId, node)
                                } else {
                                    itemRefs.current.delete(userId)
                                }
                            }}
                            key={userId ?? idx}
                        >
                            <Creator
                                creatorData={creator as any}
                                renderLocation={renderLocation}
                                selected={
                                    selectedItems?.includes(userId) ?? false
                                }
                                onSelect={() => {
                                    if (userId) {
                                        onSelect?.(userId)
                                    }
                                }}
                                onRemoveFromList={
                                    listSelection && userId
                                        ? () => onRemoveFromList?.(userId)
                                        : undefined
                                }
                                listSelection={listSelection}
                                tab={tab}
                                refetchSubscriptions={refetchSubscriptions}
                            />
                        </div>
                    )
                })
            ) : (
                <div className='creatorsList__empty'>
                    <img
                        src={noSubscriptions}
                        alt={t('noActiveSubscriptions')}
                    />

                    <div className='creatorsList__empty__text'>
                        {searchTerm && searchTerm.trim() !== '' ? (
                            <div className='creatorsList__empty__text--title'>
                                {t('noSubscriptionResults')}
                            </div>
                        ) : renderLocation === 'subscriptionsActive' ? (
                            <div className='creatorsList__empty__text--title'>
                                {t('noActiveSubscriptions')}
                            </div>
                        ) : renderLocation === 'subscriptionsExpired' ? (
                            <div className='creatorsList__empty__text--title'>
                                {t('noExpiredSubscriptions')}
                            </div>
                        ) : null}

                        <div className='creatorsList__empty__text--sub'>
                            {customPlaceholderText ??
                                t('yourSubscriptionsAppearHere')}
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default CreatorsList